/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useDateFormatter, useTranslations } from "@i18n/react";
import type { AssignmentSectionItem, Images, Section } from "@packages/sdk";
import type { JSX } from "react";
import { forwardRef } from "react";

import { spacing } from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../utils/colors/types/CSSColorStrict.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { ThumbnailProps } from "../../../blocks/other/Thumbnail/Thumbnail.emotion";
import type { ClassroomProps } from "../../_core/Classroom/Classroom.emotion";
import { Classroom } from "../../_core/Classroom/Classroom.emotion";
import { Tag } from "../../_core/Tag/Tag.emotion";
import { ContentMedium } from "../content/ContentMedium/ContentMedium.emotion";
import { ContentSmall } from "../content/ContentSmall/ContentSmall.emotion";
const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  marginTop: spacing.m,
};
const contentBoxStyle: Interpolation = {
  position: "relative",
};
const desktopOnlyStyle: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: {
    display: "flex",
  },
};
const tagStyle: Interpolation = {
  display: "flex",
  flexWrap: "wrap",
  inset: spacing.s,
  position: "absolute",
  zIndex: 1,
};
const smallStyle: Interpolation = {
  [cssMediaQueryMinWidthTablet]: {
    display: "none",
  },
};

export type AssignmentProps = WithAsChild<HallowElementProps<"div">> & {
  classroomProps?: ClassroomProps;
  color: CSSColorStrict;
  description: string;
  imageProps?: {
    src: Images;
    imageOverlay: ThumbnailProps["imageOverlay"];
  };
  item?: AssignmentSectionItem;
  section?: Partial<Section>;
  tags: string[];
  title: string;
};
export const Assignment = forwardRef<any, AssignmentProps>(
  (
    {
      asChild = false,
      classroomProps,
      color,
      children,
      description,
      imageProps,
      item,
      section,
      tags,
      title,
      ...props
    },
    ref,
  ) => {
    const t = useTranslations();
    const format = useDateFormatter();

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <Classroom
          key="classroom"
          css={section.type === "grid" && desktopOnlyStyle}
          {...classroomProps}
        />,
        <ContentMedium
          key="assignment"
          imgSrc={imageProps?.src}
          imgOverlay={imageProps?.imageOverlay}
          title={item.reference.content.title}
          description={item.reference.content.label_desc}
          metadata={item.reference.content.lengths}
          css={[contentBoxStyle, section.type === "grid" && desktopOnlyStyle]}
        >
          <>
            <div key="tags" css={tagStyle}>
              {tags.map((tag, idx) => (
                <Tag key={idx} variant="neutral">
                  {tag}
                </Tag>
              ))}
            </div>
          </>
        </ContentMedium>,
        section.type === "grid" ? (
          <ContentSmall
            key="mobileAssignment"
            imgSrc={imageProps?.src}
            imgOverlay={imageProps?.imageOverlay}
            supertitle={t("assignments_assigned_by_variable", {
              "0": item.reference.community.name,
            })}
            title={item.reference.content.title}
            css={smallStyle}
            metadata={t("assignments_due_at", {
              "0": format(new Date(item.reference.due_at), "MMM dd"),
            })}
          />
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement ref={ref} css={determinedElementStyle} {...props}>
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

Assignment.displayName = "Assignment";
